import {
  ICashierKitOptions,
  IRenderCashier,
  IApplyPay,
  IQueryPay,
  IAdyenPaymentMethods,
  IAdyenAction,
  IAdyenDisable,
  IOpenID,
  IPayHandlerParams,
} from '../types'
import axios, {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosInstance,
} from 'axios'
import { ILLEGAL_RET, OK_RET, OK_STATUS, PAYMENT_COMPLETED_RET, BASE_URL } from './const'
import { paymentChannelHandler } from '../helper/payment-handler'
import { merge, noop } from 'lodash-es'
export { store, types } from '../helper/store'
import { postMessage } from '../helper/sys-kit'
export * from '../helper/sys-kit'
import qs from 'qs'
import { initAxios } from '../plugins/axios'
export { PAYMENT_ICONS_MAPPING } from '../lib/const'
export { Looper } from '../helper/looper'
export * from '../helper/storage-provider'

export default class CashierKit {
  $options: ICashierKitOptions
  $instance: CashierKit | null
  private static $instance: CashierKit | null
  private commService: AxiosInstance
  private aggService: AxiosInstance

  private constructor(options: ICashierKitOptions) {
    const defaultOptions = {
      showLoading: noop,
      hideLoading: noop,
      loadingBeforeJump: noop,
      goToComponent: noop,
      showToast: noop,
      buildENV: '',
    }
    this.$options = merge({}, defaultOptions, options)
    this.$instance = null
    const { commService, aggService } = initAxios(options.axiosConfig || { baseURL: BASE_URL })
    this.commService = commService
    this.aggService = aggService
    commService.interceptors.response.use(this.responseResolve, this.responseError)
    aggService.interceptors.response.use(this.aggServiceResponseResolve, this.responseError)
    commService.interceptors.request.use(this.requestResolve, this.requestError)
    aggService.interceptors.request.use(this.requestResolve, this.requestError)
  }

  static getInstance(options: ICashierKitOptions): CashierKit {
    if (!this.$instance) {
      this.$instance = new CashierKit(options)
    }
    return this.$instance
  }

  private responseResolve = (response: AxiosResponse) => {
    this.$options.hideLoading()
    const { status } = response
    if (status === OK_STATUS) {
      const { ret } = response.data
      switch (ret) {
        case OK_RET:
          return response.data.data
        case ILLEGAL_RET:
          this.$options.goToComponent({ component: 'Result', type: 'illegal' })
          if (this.$options.buildENV !== 'production') {
            this.$options.showToast({ message: response.data.msg, type: 'warning' })
          }
          return Promise.reject(response)
        case PAYMENT_COMPLETED_RET:
          postMessage({ action: 'cashierOff', status: 2 })
          this.$options.showToast({ message: response.data.msg, type: 'success' })
          this.$options.goToComponent({ component: 'Result', type: 'success' })
          return Promise.reject(response)
        default:
          this.$options.showToast({
            message: response.data.msg || 'unknownServerError',
            type: 'warning',
          })
          return Promise.reject(response)
      }
    } else {
      this.$options.showToast({
        message: response.data.msg || 'unknownServerError',
        type: 'warning',
      })
    }
  }

  private aggServiceResponseResolve = (response: AxiosResponse) => {
    this.$options.hideLoading()
    const { status } = response
    if (status === OK_STATUS) {
      const { ret } = response.data
      switch (ret) {
        case OK_STATUS:
          return response.data.data
        case ILLEGAL_RET:
        case PAYMENT_COMPLETED_RET:
          window.alert(response.data.msg)
          break
        default:
          this.$options.showToast({
            message: response.data.msg || 'unknownServerError',
            type: 'warning',
          })
          return Promise.reject(response)
      }
    } else {
      this.$options.showToast({
        message: response.data.msg || 'unknownServerError',
        type: 'warning',
      })
    }
  }
  private requestResolve = (config: AxiosRequestConfig) => {
    // Do something before request is sent
    this.$options.showLoading()
    config.transformRequest = [
      function (data) {
        return qs.stringify(data)
      },
    ]
    return config
  }

  private requestError = (error: AxiosError) => Promise.reject(error)
  private responseError = (error: AxiosError) => {
    this.$options.hideLoading()
    // Do something with response error
    return Promise.reject(error)
  }

  public renderCashier = (data: IRenderCashier): AxiosPromise => {
    return this.commService.post('/pay/render', data)
  }

  public applyPay = (data: IApplyPay): AxiosPromise => {
    return this.commService.post('/pay/applyPay', data)
  }

  public queryPay = (data: IQueryPay): AxiosPromise => {
    return this.commService.post('/pay/queryPay', data)
  }

  public adyenPaymentMethods = (data: IAdyenPaymentMethods): AxiosPromise => {
    return this.commService.post('/payConfig/adyenPaymentMethods', data)
  }

  public adyenAction = (data: IAdyenAction): AxiosPromise => {
    return this.commService.post('/payConfig/adyenAction', data)
  }

  public adyenDisable = (data: IAdyenDisable): AxiosPromise => {
    return this.commService.post('/payConfig/adyenDisable', data)
  }

  public AggRenderCashier = (data: IRenderCashier): AxiosPromise => {
    return this.aggService.post('/aggregatePay/getOrderInfo', data)
  }

  public getOpenID = (data: IOpenID): AxiosPromise => {
    return this.aggService.post('/aggregatePay/wxOpenId', data)
  }

  public AggApplyPay = (data: IApplyPay): AxiosPromise => {
    return this.aggService.post('/pay/applyPay', data)
  }

  public getRegionInfo = (): any => {
    return axios
      .get('https://region-configuration.lalamove.com/index.php?env=prd')
      .then((res) => new Promise((resolve) => resolve(res.data.data)))
  }
  public payHandler = (params: IPayHandlerParams) => {
    return paymentChannelHandler(merge({}, { ctx: this.$options, applyPay: this.applyPay }, params))
  }
}
